import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RenderProps } from '../lib/slotProps';
import { normaliseMarket } from '../lib/markets';
import { DraftSlotAsset } from './useDraft';
import { toDateSafe } from '../lib/dateUtils';

export interface Run {
  id: string;
  templateId: string;
  market: string;
  status: 'queued' | 'preprocessing' | 'rendering' | 'uploading' | 'completed' | 'failed' | 'draft';
  progress: number;
  props: RenderProps;
  createdAt: Date;
  updatedAt?: Date;
  name?: string;
  /** Serialised form state — only present on draft documents. */
  slotDraft?: DraftSlotAsset[];
}

/**
 * Subscribes to all jobs for a given template, sorted most-recent-first.
 * Market filtering is done client-side to avoid requiring a composite Firestore index.
 * Returns an empty array while templateId is null.
 */
export function useRuns(templateId: string | null, market: string) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!templateId) {
      setRuns([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'jobs'),
      where('templateId', '==', templateId),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const all: Run[] = snapshot.docs.flatMap((doc) => {
          const data = doc.data();
          const status = data.status ?? 'queued';
          if (status === 'discarded' || status === 'consumed' || status === 'submitted') {
            return [];
          }

          return [{
            id: doc.id,
            templateId: data.templateId ?? templateId,
            market: normaliseMarket(data.market ?? market),
            status,
            progress: data.progress ?? 0,
            props: data.props ?? {},
            createdAt: toDateSafe(data.createdAt),
            updatedAt: data.updatedAt != null ? toDateSafe(data.updatedAt) : undefined,
            name: data.name ?? undefined,
            slotDraft: Array.isArray(data.slotDraft) ? (data.slotDraft as DraftSlotAsset[]) : undefined,
          }];
        });

        // Client-side market filter + sort by most recent first
        const filtered = all
          .filter((r) => r.market === market)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

        setRuns(filtered);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [templateId, market]);

  return { runs, loading };
}
